/**
 * haptics.js — controller vibration, per GDD 5.5.
 *
 * Patterns live in constants.js (HAPTIC_PATTERNS) so they stay tunable
 * alongside the rest of the design data. On desktop, or on any controller
 * without a haptic actuator, every call is a silent no-op — callers never need
 * to check.
 *
 * Haptics is core, not audio: it is part of the throw feel, so it lives here
 * rather than in the audio stub module.
 *
 * @module core/haptics
 */

import { HAPTIC_PATTERNS, HAPTIC_IMPACT_FULL_SCALE_N } from '../constants.js';
import { xr } from './xr.js';
import { logCall, options } from './testmode.js';

class Haptics {
  constructor() {
    this.enabled = true;
    /** @type {{left: any, right: any}} Active rumble loops. */
    this._rumble = { left: null, right: null };
  }

  /**
   * @param {'left'|'right'} side
   * @returns {GamepadHapticActuator|null}
   * @private
   */
  _actuator(side) {
    const pad = xr.getGamepad(side);
    if (!pad) return null;
    // WebXR exposes hapticActuators; some runtimes only expose vibrationActuator.
    if (pad.hapticActuators && pad.hapticActuators.length > 0) return pad.hapticActuators[0];
    if (pad.vibrationActuator) return pad.vibrationActuator;
    return null;
  }

  /**
   * Fire a raw pulse.
   * @param {'left'|'right'} side
   * @param {number} intensity 0–1
   * @param {number} durationMs
   */
  pulse(side, intensity, durationMs) {
    if (!this.enabled) return;
    if (options.test) logCall('haptics', 'pulse', [side, intensity, durationMs]);
    const act = this._actuator(side);
    if (!act) return;
    try {
      if (typeof act.pulse === 'function') act.pulse(Math.min(1, Math.max(0, intensity)), durationMs);
      else if (typeof act.playEffect === 'function') {
        act.playEffect('dual-rumble', {
          duration: durationMs, strongMagnitude: intensity, weakMagnitude: intensity * 0.6,
        });
      }
    } catch {
      // A refused actuator must never break gameplay.
    }
  }

  /**
   * Play a named pattern from GDD 5.5.
   * @param {'left'|'right'} side
   * @param {'grab'|'release'|'impact'|'miss'|'coin'|'hold'|'ui'|'score'|'teleport'} name
   * @param {{ force?: number, scale?: number }} [opts] `force` in newtons scales
   *   an impact pulse; `scale` multiplies intensity directly.
   */
  play(side, name, opts = {}) {
    const pattern = HAPTIC_PATTERNS[name];
    if (!pattern) return;
    let intensity = pattern.intensity;
    if (opts.force !== undefined) {
      intensity *= Math.min(1, Math.max(0.15, opts.force / HAPTIC_IMPACT_FULL_SCALE_N));
    }
    if (opts.scale !== undefined) intensity *= opts.scale;
    this.pulse(side, intensity, pattern.duration);
  }

  /** Both hands at once — used for two-handed impacts (High Striker). */
  playBoth(name, opts) {
    this.play('left', name, opts);
    this.play('right', name, opts);
  }

  /**
   * Start the low continuous rumble used while holding something heavy.
   * @param {'left'|'right'} side
   * @param {number} [scale=1]
   */
  startRumble(side, scale = 1) {
    if (this._rumble[side]) return;
    const p = HAPTIC_PATTERNS.hold;
    const tick = () => this.pulse(side, p.intensity * scale, p.duration);
    tick();
    this._rumble[side] = setInterval(tick, p.duration);
  }

  /** @param {'left'|'right'} side */
  stopRumble(side) {
    if (!this._rumble[side]) return;
    clearInterval(this._rumble[side]);
    this._rumble[side] = null;
  }

  /** Stop every active effect. Stalls call this on teardown. */
  stopAll() {
    this.stopRumble('left');
    this.stopRumble('right');
  }
}

/** The one haptics system. */
export const haptics = new Haptics();
